import { useId } from 'react';
import { PermissionGuard } from '@/features/auth';
import { Button } from '@/shared/components';
import { useConfirmedAction } from '../hooks/useConfirmedAction';
import { usePublishDraftMutation } from '../hooks/useDraftMutations';
import { TemplateActionConfirm } from './TemplateActionConfirm';

interface Props {
  templateId: string;
  draftVersionNumber: number;
  revision: number;
  supersedesVersionNumber: number | null;
  blockedReason: string | null;
}

export function PublishDraftAction({
  templateId,
  draftVersionNumber,
  revision,
  supersedesVersionNumber,
  blockedReason,
}: Props) {
  const hintId = useId();
  const publish = useConfirmedAction(usePublishDraftMutation(templateId));
  const message =
    supersedesVersionNumber === null
      ? `Version ${draftVersionNumber} will become the published version of this template.`
      : `Version ${draftVersionNumber} will become the published version. Version ${supersedesVersionNumber} will be superseded.`;

  return (
    <PermissionGuard permission={{ resource: 'document_template', action: 'publish' }}>
      <Button
        type="button"
        size="sm"
        ariaDisabled={blockedReason !== null}
        ariaDescribedBy={blockedReason ? hintId : undefined}
        onClick={publish.open}
      >
        Publish
      </Button>
      {blockedReason && (
        <span id={hintId} className="sr-only">
          {blockedReason}
        </span>
      )}
      <TemplateActionConfirm
        isOpen={publish.isOpen}
        title="Publish this draft?"
        message={message}
        confirmLabel="Publish"
        confirmVariant="primary"
        isPending={publish.isPending}
        error={publish.error}
        onConfirm={() => publish.confirm(revision)}
        onClose={publish.close}
      />
    </PermissionGuard>
  );
}
